// src/components/Cihazlar.js

import React, { useState, useMemo } from 'react';
import DeviceFormModal from '../components/modals/DeviceFormModal';
import DeviceTransferModal from '../modules/devices/components/DeviceTransferModal';
import GlassCard from '../ui/glass/components/GlassCard';
import usePermissions from '../hooks/usePermissions';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Stack, useTheme, useMediaQuery, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import DevicesIcon from '@mui/icons-material/Devices';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import { exportToExcel } from '../utils/exportToExcel';
import { findDuplicateSerialsInList, normalizeSerial } from '../utils/deviceDedup';
import useCachedCollection from '../hooks/useCachedCollection';

// Cihazlar Excel kolon tanımı (salt-okunur export)
const deviceExportColumns = [
  { header: 'Seri Numarası', value: (d) => d.seriNumarasi || '', width: 18 },
  { header: 'Marka', value: (d) => d.marka || '', width: 14 },
  { header: 'Model', value: (d) => d.model || '', width: 16 },
  { header: 'Cihaz Sınıfı', value: (d) => d.cihazSinifi || '', width: 18 },
  { header: 'Müşteri', value: (d) => d.musteriAdSoyad || '', width: 24 },
  { header: 'SGK', value: (d) => (d.isSgk ? 'Evet' : 'Hayır'), width: 7 },
  { header: 'Hurda', value: (d) => (d.isHurda ? 'Evet' : 'Hayır'), width: 8 },
  { header: 'Fatura No', value: (d) => d.faturaNo || '', width: 14 },
  { header: 'Ekleme Tarihi', value: (d) => d.eklenmeTarihi || d.createdAt, type: 'date', width: 14 },
];

const Cihazlar = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [transferDevice, setTransferDevice] = useState(null);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'Solunum Cihazı', 'Oksijen Cihazı', 'Diğer'
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false); // salt-okunur mükerrer seri no filtresi
  const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canDelete } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Faz 7: realtime onSnapshot yerine oturum-içi cache. Mükerrer tespiti TÜM cihaz
  // listesini gerektirdiğinden tüm koleksiyon okunur (cache'lenir, oturumda 1 kez).
  // Yazma işlemlerinden sonra refreshDevices ile listede hemen yansıtılır.
  const { data: devicesRaw, loading, refresh: refreshDevices } = useCachedCollection('devices');
  // Eski Firestore orderBy("seriNumarasi","asc") sırasını koru (cache sırasız gelir).
  const devices = useMemo(
    () => [...devicesRaw].sort((a, b) => (a.seriNumarasi || '').localeCompare(b.seriNumarasi || '', 'tr')),
    [devicesRaw]
  );

  const handleAddDevice = async (deviceData) => {
    try {
      await addDoc(collection(db, "devices"), { ...deviceData, eklenmeTarihi: serverTimestamp() });
      await refreshDevices(); // Faz 7: yeni cihaz listede hemen görünsün
      alert("Cihaz başarıyla sisteme eklendi!");
    } catch (e) { console.error("Cihaz eklenirken hata: ", e); }
  };

  const handleUpdateDevice = async (id, updatedData) => {
    try {
      const deviceRef = doc(db, 'devices', id);
      await updateDoc(deviceRef, updatedData);
      await refreshDevices(); // Faz 7: güncellemeyi listede hemen yansıt
    } catch (e) { console.error("Cihaz güncellenirken hata: ", e); }
  };

  const handleDeleteDevice = async (id, seriNumarasi) => {
    if (window.confirm(`'${seriNumarasi}' seri numaralı cihazı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      try {
        await deleteDoc(doc(db, 'devices', id));
        await refreshDevices(); // Faz 7: silinen cihaz listeden hemen düşsün
        alert("Cihaz başarıyla silindi.");
      } catch (e) {
        console.error("Cihaz silinirken hata: ", e);
        alert("Cihaz silinirken bir hata oluştu.");
      }
    }
  };

  const handleOpenEditModal = (e, device) => {
    e.stopPropagation();
    setEditingDevice(device);
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setEditingDevice(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDevice(null);
  };

  const handleOpenTransferModal = (e, device) => {
    e.stopPropagation();
    setTransferDevice(device);
    setIsTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
    setTransferDevice(null);
  };

  const handleTransferSuccess = () => {
    // Faz 7: realtime yok — devir sonrası cihaz listesini tazele ki yeni sahip görünsün.
    refreshDevices();
  };

  // Mükerrer seri no'ları (zaten yüklü listede) tespit et — ekstra okuma yapmaz.
  const { duplicateSerials } = useMemo(() => findDuplicateSerialsInList(devices), [devices]);

  const filteredDevices = devices.filter(device => {
    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch =
      (device.seriNumarasi || '').toLowerCase().includes(lowerSearch) ||
      (device.musteriAdSoyad || '').toLowerCase().includes(lowerSearch) ||
      (device.marka || '').toLowerCase().includes(lowerSearch) ||
      (device.model || '').toLowerCase().includes(lowerSearch);

    // Salt-okunur "Mükerrerler" filtresi: yalnızca seri no'su birden fazla kez geçen cihazlar
    if (showDuplicatesOnly && !duplicateSerials.has(normalizeSerial(device.seriNumarasi))) return false;

    if (filterType === 'all') return matchesSearch;
    return matchesSearch && device.cihazSinifi === filterType;
  });

  // Faz 7: ekranda yalnızca ilk `visibleCount` cihazı göster (DOM'u hafifletir).
  // Arama/filtre/mükerrer tespiti TÜM cihazlar üzerinde çalışır; export da tüm filtreli veriyi indirir.
  const visibleDevices = filteredDevices.slice(0, visibleCount);

  // Excel export (salt-okunur) — aktif filtre/aramaya uyan cihazları indirir
  const handleExportVisible = () => {
    if (filteredDevices.length === 0) {
      alert('Dışa aktarılacak cihaz bulunamadı.');
      return;
    }
    exportToExcel({ rows: filteredDevices, columns: deviceExportColumns, sheetName: 'Cihazlar', fileName: 'Cihaz_Listesi', scopeLabel: 'Goruntulenen' });
  };

  // Excel export (salt-okunur) — filtreden bağımsız tüm cihazları indirir
  const handleExportAll = () => {
    if (devices.length === 0) {
      alert('Dışa aktarılacak cihaz bulunamadı.');
      return;
    }
    exportToExcel({ rows: devices, columns: deviceExportColumns, sheetName: 'Cihazlar', fileName: 'Cihaz_Listesi', scopeLabel: 'Tumu' });
  };

  if (loading) { return <Loading message="Cihazlar yükleniyor..." />; }

  return (
    <Box sx={{ p: { xs: 1, md: 1 } }}>
      <PageHeader
        title="Cihaz Yönetimi"
        subtitle="Tüm cihazların takibi ve yönetimi"
        icon={<DevicesIcon />}
      >
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={(e, newFilter) => { if (newFilter) setFilterType(newFilter); }}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', flexWrap: 'wrap' }}
            >
              <ToggleButton value="all">Tümü</ToggleButton>
              <ToggleButton value="Oksijen Konsantratörü">Oksijen Kons.</ToggleButton>
              <ToggleButton value="Ventilatör">Ventilatör</ToggleButton>
              <ToggleButton value="Cpap">Cpap</ToggleButton>
              <ToggleButton value="Bpap">Bpap</ToggleButton>
              <ToggleButton value="Diğer">Diğer</ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" variant="outlined" placeholder="Seri No, Müşteri veya Marka Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
            <Button
              variant={showDuplicatesOnly ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<ContentCopyIcon />}
              onClick={() => setShowDuplicatesOnly(v => !v)}
            >
              {showDuplicatesOnly ? 'Tümünü Göster' : `Mükerrerler (${duplicateSerials.size})`}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshDevices} disabled={loading}>Yenile</Button>
            <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />} onClick={handleExportVisible}>Görüneni İndir</Button>
            <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />} onClick={handleExportAll}>Tümünü İndir</Button>
            {currentUser && currentUser.role !== 'Servis Teknisyeni' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal}>Yeni Cihaz Ekle</Button>
            )}
      </PageHeader>
      <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0, bgcolor: isMobile ? 'transparent' : undefined, boxShadow: isMobile ? 'none' : undefined, backdropFilter: isMobile ? 'none' : undefined, border: isMobile ? 'none' : undefined }}>
        {loading ? (
          <Loading />
        ) : isMobile ? (
          // MOBILE CARD VIEW
          <Stack spacing={2}>
            {visibleDevices.map((device) => (
              <GlassCard key={device.id} onClick={() => navigate(`/cihaz/${device.id}`)} sx={{ p: 2, cursor: 'pointer', '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.1s' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {device.seriNumarasi}
                      {device.isHurda && <StatusChip label="HURDA" color="error" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                      {duplicateSerials.has(normalizeSerial(device.seriNumarasi)) && <StatusChip label="MÜKERRER" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{device.marka} {device.model}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.1)', px: 1, py: 0.5, borderRadius: 1 }}>{device.cihazSinifi}</Typography>
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <Typography component="span" fontWeight="bold">Müşteri:</Typography> {device.musteriAdSoyad}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }} onClick={(e) => e.stopPropagation()}>
                  {currentUser?.role !== 'Servis Teknisyeni' && (
                    <>
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={(e) => handleOpenEditModal(e, device)}>Düzenle</Button>
                      <Button size="small" variant="outlined" color="warning" onClick={(e) => handleOpenTransferModal(e, device)}>Devret</Button>
                    </>
                  )}
                  {canDelete() && (
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteDevice(device.id, device.seriNumarasi); }}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Box>
              </GlassCard>
            ))}
            {filteredDevices.length === 0 && (
              <EmptyState
                icon={<DevicesIcon />}
                message="Cihaz bulunamadı."
                description={searchTerm || filterType !== 'all' || showDuplicatesOnly ? 'Arama/filtre kriterlerinizle eşleşen cihaz yok.' : 'Yeni bir cihaz ekleyerek başlayın.'}
              />
            )}
            {visibleCount < filteredDevices.length && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                <Typography variant="caption" color="text.secondary">{visibleDevices.length} / {filteredDevices.length} gösteriliyor</Typography>
                <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
              </Box>
            )}
          </Stack>
        ) : (
          // DESKTOP TABLE VIEW
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Seri Numarası</TableCell>
                  <TableCell>Marka</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Cihaz Sınıfı</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleDevices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<DevicesIcon />}
                        message="Cihaz bulunamadı."
                        description={searchTerm || filterType !== 'all' || showDuplicatesOnly ? 'Arama/filtre kriterlerinizle eşleşen cihaz yok.' : 'Yeni bir cihaz ekleyerek başlayın.'}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {visibleDevices.map((device) => (
                  <TableRow hover key={device.id} onClick={() => navigate(`/cihaz/${device.id}`)} sx={{ cursor: 'pointer', bgcolor: device.isHurda ? 'rgba(211, 47, 47, 0.04)' : 'inherit' }}>
                    <TableCell>
                      {device.seriNumarasi}
                      {device.isHurda && <StatusChip label="HURDA" color="error" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                      {duplicateSerials.has(normalizeSerial(device.seriNumarasi)) && <StatusChip label="MÜKERRER" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                    </TableCell>
                    <TableCell>{device.marka}</TableCell>
                    <TableCell>{device.model || '-'}</TableCell>
                    <TableCell>{device.cihazSinifi || '-'}</TableCell>
                    <TableCell>{device.musteriAdSoyad}</TableCell>
                    <TableCell>
                      {currentUser && currentUser.role !== 'Servis Teknisyeni' && (
                        <>
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} sx={{ mr: 1 }} onClick={(e) => handleOpenEditModal(e, device)}>Düzenle</Button>
                          <Button size="small" variant="outlined" color="warning" sx={{ mr: 1 }} onClick={(e) => handleOpenTransferModal(e, device)}>Devret</Button>
                        </>
                      )}
                      {canDelete() && (
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); handleDeleteDevice(device.id, device.seriNumarasi); }}>Sil</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && !isMobile && visibleCount < filteredDevices.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
            <Typography variant="caption" color="text.secondary">{visibleDevices.length} / {filteredDevices.length} gösteriliyor</Typography>
            <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
          </Box>
        )}
      </GlassCard>
      <DeviceFormModal isOpen={isModalOpen} onClose={handleCloseModal} onAddDevice={handleAddDevice} onUpdateDevice={handleUpdateDevice} deviceToEdit={editingDevice} />
      <DeviceTransferModal isOpen={isTransferModalOpen} onClose={handleCloseTransferModal} device={transferDevice} currentCustomerId={transferDevice?.musteriId} onTransferSuccess={handleTransferSuccess} />
    </Box>
  );
};

export default Cihazlar;